import { useMemo } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { MonacoPane, type MonacoMarker } from './MonacoPane';

/**
 * Shared layout for the format-three-piece set (yaml / xml / sql) and
 * any future "left input → right output" formatter that doesn't need
 * the rich tree explorer JSON has.
 *
 * Top toolbar slot (`toolbar`) is page-specific — each tool drops its
 * own format / minify / dialect select / 缩进 controls there. This
 * component owns the structural concerns: the two-column grid, the
 * error footer, the Monaco mounting, and the height stretch. Pages
 * stay declarative.
 *
 * Why grid not flex: same reason as JsonTool — `lg:grid-rows-1`
 * resolves to `repeat(1, minmax(0, 1fr))` which forces an explicit row
 * height computed BEFORE descendants. flex-row + align-items: stretch
 * has a known browser quirk where a child's `min-height` (from
 * Monaco's 240px floor) makes the column stop honoring stretch and
 * collapse to its intrinsic size, leaving the two columns uneven.
 */
export interface DualPaneFormatterProps {
  /** Monaco language id for the input pane (e.g. 'yaml', 'xml', 'sql'). */
  language: string;

  /** Left pane state. */
  inputValue: string;
  onInputChange: (next: string) => void;
  inputPlaceholder?: string;

  /** Right pane content (read-only by default). */
  outputValue: string;
  /**
   * Override output language when it differs from input (e.g. yaml→json).
   * Falls back to `language` when omitted.
   */
  outputLanguage?: string;
  /** Markers for the OUTPUT pane (rare; usually errors point at input). */
  outputMarkers?: MonacoMarker[];

  /**
   * Filename used by the output pane's download button. If omitted,
   * MonacoPane derives one from `outputLanguage ?? language` plus the
   * pane title — usually fine, but tools that want a stable
   * "manifest.yaml" / "schema.xml" should pass it explicitly.
   */
  outputFilename?: string;

  /** Top toolbar — page-specific buttons / selects. */
  toolbar: React.ReactNode;

  /**
   * Inline error from the parser / formatter. Renders a dismissible
   * footer below both panes. `line` / `column` (1-based) populates a
   * Monaco marker on the input pane so the user gets a red squiggle at
   * the exact spot in addition to the human-readable footer.
   */
  error?: { message: string; line?: number; column?: number };

  /**
   * Optional success message footer (e.g. "已美化 12 KB → 14 KB"). Only
   * shown when no error and string is non-empty.
   */
  successHint?: string;
}

export function DualPaneFormatter({
  language,
  inputValue,
  onInputChange,
  inputPlaceholder,
  outputValue,
  outputLanguage,
  outputMarkers,
  outputFilename,
  toolbar,
  error,
  successHint,
}: DualPaneFormatterProps): JSX.Element {
  const inputMarkers: MonacoMarker[] = useMemo(() => {
    if (!error || !error.line) return [];
    return [
      {
        line: error.line,
        column: error.column ?? 1,
        endColumn: (error.column ?? 1) + 1,
        message: error.message,
        severity: 'error',
      },
    ];
  }, [error]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* Toolbar strip */}
      <div className="flex flex-wrap items-center gap-2">{toolbar}</div>

      {/* Two-column workspace; stacks under lg. Each column is a
       * MonacoPane whose own <section> serves as the bordered column
       * surface — no outer wrapper, no double border.                 */}
      <div className="grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2 lg:grid-rows-1">
        <MonacoPane
          title="输入"
          value={inputValue}
          onChange={onInputChange}
          language={language}
          placeholder={inputPlaceholder}
          markers={inputMarkers}
          actions={['clear', 'format']}
          minHeight={0}
          className="min-h-0 min-w-0"
        />
        <MonacoPane
          title="输出"
          value={outputValue}
          language={outputLanguage ?? language}
          readOnly
          actions={['copy', 'download']}
          markers={outputMarkers}
          downloadFilename={outputFilename}
          minHeight={0}
          className="min-h-0 min-w-0"
        />
      </div>

      {/* Footer: error or success hint. */}
      {error ? (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] text-rose-700 dark:text-rose-300">
          <AlertCircle className="mr-1 inline h-3 w-3" />
          {error.line ? (
            <span className="font-mono">第 {error.line} 行 {error.column ?? 1} 列：</span>
          ) : null}
          <span>{error.message}</span>
        </p>
      ) : successHint ? (
        <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[12px] text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="mr-1 inline h-3 w-3" />
          {successHint}
        </p>
      ) : null}
    </div>
  );
}
